import React, {Suspense, useReducer} from 'react';
import {CompetitionCaptionDouble, Subheading} from 'app/styles/heading';
import {ContentWithStickyBanner} from 'app/styles/content';
import {formatLongDate} from 'app/helpers/dateHelpers';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {LoadingIndicator, LOADING_MESSAGE, Placeholder} from '@fupa/fupa-uikit';
import {Helmet} from 'react-helmet-async';
import {object} from 'prop-types';
import {fetchMatchDuels} from 'app/routes/match/MatchDuelsPageActions';
import {MatchDuelsPageReducer} from 'app/routes/match/MatchDuelsPageReducer';
import {useInfiniteScrollComponentState} from 'app/hooks/useInfiniteScroll';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import NotFoundPage from 'app/routes/error/NotFoundPage';
import {useFetchData, useRestoreData} from 'app/hooks/dataHooks';
import {MatchRow} from 'app/components/match/matchRow/MatchRow';
import ResizeObserver from 'resize-observer-polyfill';
import {useConstructor} from 'app/hooks/useConstructor';
import {windowIsDefined} from 'app/helpers/windowIsDefined';
import {selectHydrationFinished} from 'app/hooks/reduxCreateSelectorHooks';
import {useParams} from 'react-router';
const TeamComparisonChart = React.lazy(() => import('app/components/team/TeamComparisonChart'));

const ChartPlaceholderWrapper = styled.div`
  width: 100%;
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SCSubheading = styled(Subheading)`
  &&& {
    margin-bottom: 0;
  }
`;
const getMetaInformation = (homeTeamName, awayTeamName) => {
  const title = `${homeTeamName} - ${awayTeamName} - Alle Duelle${TITLE_POSTFIX}`;
  const meta = [
    {
      name: 'description',
      content: `Liveticker, Aufstellungen und die Duell-Bilanz zum Spiel ${homeTeamName} - ${awayTeamName}`,
    },
    {
      name: 'robots',
      content: 'noindex, nofollow',
    },
  ];

  return {title, meta};
};

const MatchDuelsPage = ({matchInfo}) => {
  const {restoredData} = useRestoreData('MatchDuelsPage');
  const {matchSlug} = useParams();
  const {homeTeam, awayTeam} = matchInfo;

  const initialState = restoredData ?? {
    stats: {
      isFetching: false,
      data: {},
      error: null,
    },
    matches: {
      isFetching: false,
      items: [],
      nextUrl: '',
      error: null,
    },
  };
  const [state, dispatch] = useReducer(MatchDuelsPageReducer, initialState);
  const dispatchRedux = useDispatch();
  const hydrationFinished = useSelector(selectHydrationFinished);

  useConstructor(() => {
    if (windowIsDefined && !window.ResizeObserver) {
      window.ResizeObserver = ResizeObserver;
    }
  });

  const loadData = reloading => fetchMatchDuels(matchSlug, dispatchRedux, reloading)(dispatch, state);

  const {
    stats: {data: stats, error: statsError},
    matches: {isFetching: isFetchingMatches, items: matches, error: matchesError},
  } = state;
  useInfiniteScrollComponentState(loadData, matches.length);
  useFetchData(state, loadData, 'MatchDuelsPage');

  if (statsError && matchesError) {
    const id = statsError.status === 404 || matchesError.status === 404 ? 'not-found-page' : '';
    return <NotFoundPage id={id} />;
  }

  const {title, meta} =
    homeTeam && awayTeam ? getMetaInformation(homeTeam.name.full, awayTeam.name.full) : {title: '', meta: []};

  const pastMatchRows =
    matches.length && homeTeam ? (
      matches.map(match => {
        const caption = (
          <CompetitionCaptionDouble variant='caption1'>
            <span>{match.competition?.middleName}</span>
            <span>{formatLongDate(match.kickoff)}</span>
          </CompetitionCaptionDouble>
        );
        return (
          <React.Fragment key={match.slug}>
            <MatchRow
              standalone={true}
              caption={caption}
              match={match}
              colored={true}
              ownClubSlug={homeTeam.clubSlug}
            />
          </React.Fragment>
        );
      })
    ) : (
      <Placeholder>Es liegen keine vergangenen Spiele dieser beiden Mannschaften vor.</Placeholder>
    );

  const showLoadingMatches = isFetchingMatches ? (
    <LoadingIndicator message={LOADING_MESSAGE.loadingMatches} bgColor={true} />
  ) : null;

  const ChartPlaceholder = (
    <ChartPlaceholderWrapper>
      <LoadingIndicator message={'Grafik wird geladen...'} bgColor={false} />
    </ChartPlaceholderWrapper>
  );

  const chart = hydrationFinished ? (
    <Suspense fallback={ChartPlaceholder}>
      <TeamComparisonChart homeTeam={homeTeam} awayTeam={awayTeam} statistics={stats} />
    </Suspense>
  ) : (
    ChartPlaceholder
  );

  return (
    <ContentWithStickyBanner>
      <Helmet title={title} meta={meta} />
      <SCSubheading variant='subheading2'>Bilanz - Pflichtspiele</SCSubheading>
      {chart}
      <Subheading variant='subheading2'>Bisherige Spiele</Subheading>
      {pastMatchRows}
      {showLoadingMatches}
    </ContentWithStickyBanner>
  );
};

MatchDuelsPage.propTypes = {
  match: object.isRequired,
};

export default MatchDuelsPage;
